import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, onSnapshot, query, where, orderBy, doc, updateDoc, writeBatch, deleteDoc } from "firebase/firestore";

const S = {
  badge:(bg,c)=>({display:"inline-block",fontSize:10,padding:"2px 7px",borderRadius:10,background:bg,color:c,fontWeight:500}),
  btn:{fontSize:12,padding:"5px 12px",borderRadius:8,border:"0.5px solid #ccc",background:"#fff",cursor:"pointer",fontFamily:"sans-serif"},
};

function formatTime(ts){
  if(!ts) return "";
  const d = new Date(ts);
  const now = new Date();
  const diff = now - d;
  if(diff < 60000) return "just now";
  if(diff < 3600000) return Math.floor(diff/60000)+"m ago";
  if(diff < 86400000) return d.toLocaleTimeString("en-US",{hour:"numeric",minute:"2-digit"});
  return d.toLocaleDateString("en-US",{month:"short",day:"numeric"});
}

const TYPE_INFO = {
  document_request:  { icon:"📄", label:"Document Request",   color:"#854F0B",  bg:"#FAEEDA" },
  new_message_to_client: { icon:"💬", label:"New Message",    color:"#185FA5",  bg:"#E6F1FB" },
  client_uploaded:   { icon:"⬆",  label:"Files Uploaded",    color:"#3B6D11",  bg:"#EAF3DE" },
  client_responded:  { icon:"✅",  label:"Request Completed", color:"#3B6D11",  bg:"#EAF3DE" },
  client_message:    { icon:"💬",  label:"Client Message",    color:"#185FA5",  bg:"#E6F1FB" },
  task_assigned:     { icon:"📋",  label:"Task Assigned",     color:"#534AB7",  bg:"#EEEDFE" },
};

export default function Alerts({ isAdmin, onNavigate }) {
  const [clientAlerts, setClientAlerts] = useState([]);
  const [teamAlerts, setTeamAlerts] = useState([]);
  const [activeTab, setActiveTab] = useState("client");

  useEffect(()=>{
    const unsubs = [];
    if(isAdmin){
      // Client alerts — things clients did
      unsubs.push(onSnapshot(
        query(collection(db,"notifications"), where("forAdmin","==",true), orderBy("sentAt","desc")),
        snap=>setClientAlerts(snap.docs.map(d=>({...d.data(),firestoreId:d.id})))
      ));
      // Team alerts — tasks assigned TO Frank by team members
      unsubs.push(onSnapshot(
        query(collection(db,"notifications"), where("forAdmin","==",true), where("type","==","task_assigned"), orderBy("sentAt","desc")),
        snap=>setTeamAlerts(snap.docs
          .map(d=>({...d.data(),firestoreId:d.id}))
          .filter(a=>a.recipientEmail==="frank@ficsoriaccounting.com")
        )
      ));
    }
    return ()=>unsubs.forEach(u=>u());
  },[isAdmin]);

  const markRead = async (id) => {
    try{ await updateDoc(doc(db,"notifications",id),{read:true}); }catch(e){}
  };

  const dismissAlert = async (id) => {
    try{ await deleteDoc(doc(db,"notifications",id)); }catch(e){}
  };

  const markAllRead = async (alerts) => {
    const batch = writeBatch(db);
    alerts.filter(a=>!a.read).forEach(a=>batch.update(doc(db,"notifications",a.firestoreId),{read:true}));
    try{ await batch.commit(); }catch(e){}
  };

  const unreadClient = clientAlerts.filter(a=>!a.read&&a.type!=="task_assigned").length;
  const unreadTeam = teamAlerts.filter(a=>!a.read).length;

  const displayAlerts = activeTab==="client"
    ? clientAlerts.filter(a=>a.type!=="task_assigned")
    : teamAlerts;

  const getNavTarget = (alert) => {
    const type = alert.type;
    const details = alert.details||"";
    if(type==="client_uploaded") return "files";
    if(type==="client_responded") return "tasks";
    if(type==="task_assigned"){
      // If details mention a channel (#general etc), it's a team chat message
      if(details.startsWith("#")) return "team-chat";
      return "tasks";
    }
    if(type==="client_message") return "messages";
    return "dashboard";
  };

  return (
    <div>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:14}}>
        <div style={{fontSize:14,fontWeight:500}}>Alerts & Notifications</div>
        {displayAlerts.filter(a=>!a.read).length>0&&(
          <button style={{...S.btn,fontSize:11}} onClick={()=>markAllRead(displayAlerts)}>Mark all read</button>
        )}
      </div>

      {/* Tabs */}
      <div style={{display:"flex",gap:6,marginBottom:14}}>
        {[
          ["client","👥 Client Alerts", unreadClient],
          ["team","👤 Team Alerts", unreadTeam],
        ].map(([tab,label,count])=>(
          <button key={tab} onClick={()=>setActiveTab(tab)} style={{
            fontSize:12,padding:"6px 14px",borderRadius:8,cursor:"pointer",fontFamily:"sans-serif",
            background:activeTab===tab?"#C0111F":"#fff",
            color:activeTab===tab?"#fff":"#555",
            border:"0.5px solid "+(activeTab===tab?"#C0111F":"#ccc"),
            display:"flex",alignItems:"center",gap:6,
          }}>
            {label}
            {count>0&&<span style={{background:activeTab===tab?"rgba(255,255,255,0.3)":"#C0111F",color:"#fff",fontSize:10,padding:"1px 5px",borderRadius:10}}>{count}</span>}
          </button>
        ))}
      </div>

      {/* Alerts list */}
      {displayAlerts.length===0?(
        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,padding:"40px 0",textAlign:"center"}}>
          <div style={{fontSize:28,marginBottom:8}}>🔔</div>
          <div style={{fontSize:13,color:"#888"}}>No {activeTab} alerts yet</div>
        </div>
      ):(
        <div style={{background:"#fff",border:"0.5px solid #e0dfd8",borderRadius:12,overflow:"hidden"}}>
          {displayAlerts.map(alert=>{
            const info = TYPE_INFO[alert.type]||{icon:"🔔",label:alert.type,color:"#555",bg:"#f5f5f0"};
            return (
              <div key={alert.firestoreId}
                onClick={()=>{ markRead(alert.firestoreId); if(onNavigate) onNavigate(getNavTarget(alert)); }}
                style={{cursor:"pointer",
                  display:"flex",alignItems:"flex-start",gap:12,padding:"14px 16px",
                  borderBottom:"0.5px solid #f0efe8",
                  background:alert.read?"#fff":"#fafaf8",
                  borderLeft:alert.read?"2px solid transparent":"2px solid #C0111F",
                }}
                onMouseEnter={e=>e.currentTarget.style.background="#f5f5f0"}
                onMouseLeave={e=>e.currentTarget.style.background=alert.read?"#fff":"#fafaf8"}
              >
                <div style={{width:36,height:36,borderRadius:"50%",background:info.bg,color:info.color,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,flexShrink:0}}>
                  {info.icon}
                </div>
                <div style={{flex:1,minWidth:0}}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:3}}>
                    <div style={{fontSize:12,fontWeight:alert.read?400:600}}>
                      {alert.clientName||alert.senderName||"System"}
                      {!alert.read&&<span style={{width:6,height:6,borderRadius:"50%",background:"#C0111F",display:"inline-block",marginLeft:6,verticalAlign:"middle"}}/>}
                    </div>
                    <span style={{fontSize:10,color:"#aaa",flexShrink:0,marginLeft:8}}>{formatTime(alert.sentAt)}</span>
                  </div>
                  <div style={{fontSize:12,color:"#555",marginBottom:4}}>{alert.details||info.label}</div>
                  <span style={S.badge(info.bg,info.color)}>{info.label}</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Export unread counts for badge display
export function useAlertCounts() {
  const [clientCount, setClientCount] = useState(0);
  const [teamCount, setTeamCount] = useState(0);

  useEffect(()=>{
    const unsubs = [];
    unsubs.push(onSnapshot(
      query(collection(db,"notifications"), where("forAdmin","==",true), where("read","==",false)),
      snap=>{
        const alerts = snap.docs.map(d=>d.data());
        setClientCount(alerts.filter(a=>a.type!=="task_assigned").length);
        setTeamCount(alerts.filter(a=>a.type==="task_assigned"&&a.recipientEmail==="frank@ficsoriaccounting.com").length);
      }
    ));
    return ()=>unsubs.forEach(u=>u());
  },[]);

  return { clientCount, teamCount, total: clientCount + teamCount };
}
